import { notificationTypeLabel } from "../entities/notifications/labels";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useMemo, useRef, useState } from "react";
import { HiChevronDown } from "react-icons/hi2";

import { notificationItems } from "../entities/notifications/list";
import type { NotificationPreferenceInput, SystemNotification } from "../entities/notifications";
import { isStandalone } from "../pwa/install";
import {
  WebPushManager,
  connectFailure,
  currentPlatform,
  deliveryReport,
  describeDevice,
} from "../web-push";
import { NotificationDetail } from "./NotificationCenter";
import { NotificationDeviceHelp } from "./NotificationDeviceHelp";
import { NotificationDiagnostics } from "./NotificationDiagnostics";
import type { ShellServices } from "./services";
import { Notice } from "./Notice";

type NotificationServices = ShellServices & Required<Pick<ShellServices, "apiClient" | "notifications">>;

function channelLabel(channel: "in_app" | "realtime" | "web_push"): string {
  return { in_app: "В центре", realtime: "В браузере", web_push: "На устройство" }[channel];
}

/** «Подключено сегодня» читается лучше, чем машинная дата. */
function connectedAt(value: string | null): string {
  if (value === null) return "Дата подключения неизвестна";
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000);
  if (days <= 0) return "Подключено сегодня";
  if (days === 1) return "Подключено вчера";
  return `Подключено ${new Intl.DateTimeFormat("ru-RU", { dateStyle: "long" }).format(new Date(value))}`;
}

/**
 * Когда устройство в последний раз получило уведомление с сервера.
 *
 * Отметку ставит сам сервер при успешной отправке, поэтому «ни разу» — это не
 * догадка, а признак устройства, до которого уведомления не доходят (старый
 * дубль после переустановки, отозванная подписка). По этой строке видно, что
 * проверка дошла до каждого устройства, а не только до того, где нажали.
 */
function deliveredAt(value: string | null): string {
  if (value === null) return "Уведомления сюда ещё не доходили";
  return `Последнее уведомление получило: ${new Intl.DateTimeFormat("ru-RU", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))}`;
}

/**
 * Блок управления уведомлениями над лентой.
 *
 * Раньше настроек не было вовсе: диалог в коде существовал, но кнопки, которая
 * его открывает, не было — открыть его было нечем. Блок стоит здесь же, а не в
 * отдельном разделе: на страницу уведомлений человек приходит именно тогда,
 * когда хочет с ними что-то сделать.
 */
function NotificationSettings({
  services,
  defaultExpanded,
}: {
  services: NotificationServices;
  defaultExpanded: boolean;
}) {
  const queryClient = useQueryClient();
  const api = services.notifications;
  const manager = useMemo(() => new WebPushManager(api), [api]);
  const [open, setOpen] = useState(false);
  // Блок объёмный, а заходят на страницу за самими уведомлениями: по умолчанию он
  // свёрнут до одной строки, и на телефоне до ленты не нужно пролистывать настройки.
  const [expanded, setExpanded] = useState(defaultExpanded);
  const panelId = useId();
  const [notice, setNotice] = useState<string | null>(null);
  // Отложенный итог проверки: новая проверка отменяет прежний, чтобы итог не
  // затёр более свежее сообщение.
  const verification = useRef<number | undefined>(undefined);
  // Общее правило с включённой доставкой на устройство — то, что предлагаем
  // одной кнопкой, когда проверка показала, что серверные уведомления не уходят.
  const [offeredGeneral, setOfferedGeneral] = useState<NotificationPreferenceInput | null>(null);
  // Если браузер уже отказал, человеку нужна подсказка, а не догадки: открываем её сами.
  const [helpOpen, setHelpOpen] = useState(() => manager.permission() === "denied");

  const preferences = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: () => api.preferences(),
    retry: false,
  });
  // Подписка принадлежит устройству, поэтому спрашиваем и браузер, и сервер:
  // браузер знает про это устройство, сервер — куда он вообще шлёт.
  const devices = useQuery({
    queryKey: ["push-subscriptions"],
    queryFn: async () => {
      const [current, registered] = await Promise.all([
        manager.currentFingerprint(),
        api.pushSubscriptions(),
      ]);
      return { current, items: registered.data };
    },
    retry: false,
  });

  // Конфигурацию доставки читаем заранее, а не по нажатию: Safari на iPhone
  // засчитывает подписку только рядом с самим жестом, а запрос по мобильной
  // сети между нажатием и подпиской этот промежуток съедает.
  const deliveryConfig = useQuery({
    queryKey: ["notification-delivery-config"],
    queryFn: () => api.realtimeConfig(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["notification-preferences"] }),
      queryClient.invalidateQueries({ queryKey: ["push-subscriptions"] }),
    ]);

  const toggle = useMutation({
    mutationFn: (value: NotificationPreferenceInput) => api.updatePreferences([value]),
    onSuccess: () => refresh(),
    onError: () => setNotice("Не удалось сохранить настройку. Повторите попытку."),
  });
  const connect = useMutation({
    mutationFn: async () => {
      // Разрешение — до запроса к серверу: на iPhone диалог открывается только
      // прямо из нажатия, а после ожидания сети оно уже не считается нажатием.
      await manager.ensurePermission();
      const key =
        deliveryConfig.data === undefined
          ? (await api.realtimeConfig()).data.vapid_public_key
          : deliveryConfig.data.data.vapid_public_key;
      if (key === null) {
        throw new Error("Уведомления на устройство сервером пока не настроены.");
      }
      // Имя для списка устройств: «Chrome · Windows», а не сырой user agent.
      return manager.subscribe(
        key,
        describeDevice(
          {
            userAgent: navigator.userAgent,
            maxTouchPoints: navigator.maxTouchPoints,
          },
          isStandalone(),
        ),
      );
    },
    onSuccess: async () => {
      setNotice("Устройство подключено. Уведомления будут приходить, даже когда ERP закрыта.");
      await refresh();
    },
    onError: (error) => {
      const failure = connectFailure(error, {
        permission: manager.permission(),
        platform: currentPlatform(),
        installed: isStandalone(),
      });
      setNotice(failure.message);
      if (failure.showHelp) setHelpOpen(true);
    },
  });
  const disconnect = useMutation({
    mutationFn: ({ id, here }: { id: string; here: boolean }) =>
      here ? manager.unsubscribeHere(id) : manager.forget(id),
    onSuccess: async () => {
      setNotice("Устройство отключено.");
      await refresh();
    },
    onError: () => setNotice("Не удалось отключить устройство. Повторите попытку."),
  });

  /**
   * Проверка доставки.
   *
   * Цель — узнать заранее, заметят ли важное уведомление, когда система
   * заработает. Поэтому проверка идёт тем же путём, что и настоящие: сервер
   * рассылает уведомление на все подключённые устройства человека, а не на то,
   * где нажали кнопку. Окно, которое браузер рисует сам, показывает только
   * это устройство, и оно подменяло бы результат: на телефоне «пришло», хотя
   * на остальные ничего не ушло. Поэтому, пока сервер шлёт и на это устройство,
   * своё окно не рисуем — остаётся его запасная роль для неподключённых.
   */
  const probe = useMutation({
    mutationFn: async () => {
      // Сервер шлёт на устройство только по настройке: у проверочного события
      // своей строки нет, оно берёт значение из «Всех событий», а по умолчанию
      // там выключено. Читаем настройку заново, а не из кеша: её могли изменить
      // на другом устройстве, а обещать нужно то, что произойдёт сейчас.
      const fresh = await api.preferences().catch(() => null);
      const general = fresh?.data.preferences.find((row) => row.type === "*") ?? null;
      // Серверное уведомление дойдёт и сюда — тогда собственное окно браузера
      // не нужно. Иначе оно остаётся единственным способом проверить, что
      // браузер и система вообще что-то показывают.
      const serverReachesHere = subscribedHere && general?.web_push !== false;
      if (!serverReachesHere) await manager.showLocalProbe();
      await services.operations!.testNotification();

      return { general };
    },
    onSuccess: async ({ general: fresh }) => {
      setOfferedGeneral(null);
      if (manager.permission() === "unsupported") {
        // Не отказ, а невозможность: обвинять разрешение здесь неправда.
        setHelpOpen(true);
        setNotice(
          currentPlatform() === "ios" && !isStandalone()
            ? "Уведомление отправлено и лежит в центре уведомлений. Всплывающих не будет: Safari на iPhone и iPad не показывает их вне установленного приложения. Установите ERP на экран «Домой» — шаги ниже."
            : "Уведомление отправлено и лежит в центре уведомлений. Всплывающих не будет: этот браузер или адрес не поддерживает уведомления при закрытой вкладке.",
        );
      } else if (manager.permission() !== "granted") {
        setHelpOpen(true);
        setNotice(
          "Уведомление отправлено и появится в центре уведомлений. Всплывающих не будет: браузер не дал разрешение показывать их. Ниже — как это исправить.",
        );
      } else if (!subscribedHere) {
        const others = items.map((device) => device.device_name ?? "устройство без имени");
        setNotice(
          `Проверка показа появилась только на этом устройстве: оно не подключено, поэтому серверные уведомления сюда не идут. ${
            others.length > 0
              ? `Уведомление с сервера ушло на подключённые устройства: ${others.join(", ")}.`
              : "Ни одно устройство пока не подключено — уведомление лежит только в центре уведомлений."
          } Чтобы такие приходили и сюда, подключите это устройство.`,
        );
      } else if (fresh !== null && !fresh.web_push) {
        // Всплывающее будет одно — и это не поломка, а выключенная доставка.
        // Говорим об этом прямо и даём включить её здесь же.
        setOfferedGeneral({
          type: "*",
          in_app: fresh.in_app,
          realtime: fresh.realtime,
          web_push: true,
        });
        setNotice(
          "Проверка показа появилась — значит, браузер и система уведомления показывают. Уведомление с сервера лежит в центре уведомлений, но на устройство не отправляется: во «Всех событиях» выключена доставка «На устройство».",
        );
      } else {
        // Одно и то же серверное уведомление — на всех подключённых устройствах,
        // включая то, где нажали кнопку. Называем их: видно, чего ждать и где.
        const names = items.map((device) => device.device_name ?? "устройство без имени");
        setNotice(
          `Отправлено на все подключённые устройства: ${names.join(", ")}. Уведомление идёт с сервера через очередь и может задержаться на несколько секунд — оно должно появиться на каждом, в том числе на этом. В списке устройств ниже через несколько секунд появится время, когда каждое его получило; у устройства без отметки уведомления не доходят — отключите его и подключите заново.`,
        );
        // Результат по устройствам виден в списке: открываем его и обновляем, пока
        // сервер отправляет и отмечает доставку.
        setOpen(true);
        // Через 20 секунд сверяем отметки доставки с теми, что были до проверки, и
        // называем итог: приняла ли push-служба сообщение и для каких устройств.
        const before = new Map(items.map((device) => [device.id, device.last_used_at]));
        window.clearTimeout(verification.current);
        verification.current = window.setTimeout(() => {
          void devices.refetch().then((result) => {
            const now = new Map((result.data?.items ?? []).map((device) => [device.id, device.last_used_at]));
            const report = deliveryReport(
              items.map((device) => ({
                name: device.device_name ?? "устройство без имени",
                before: before.get(device.id) ?? null,
                after: now.has(device.id) ? (now.get(device.id) ?? null) : undefined,
              })),
            );
            if (report.message !== "") setNotice(report.message);
          });
        }, 20_000);
      }
      await queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: () =>
      setNotice("Не удалось отправить проверочное уведомление. Повторите попытку."),
  });

  const rows = (preferences.data?.data.preferences ?? []).map((row) => ({ ...row, label: notificationTypeLabel(row.type, row.label) }));
  const general = rows.find((row) => row.type === "*");
  const items = devices.data?.items ?? [];
  // Подключённым считаем устройство, о котором знает и браузер, и сервер:
  // подписка в браузере могла остаться от другого сотрудника, и сервер о ней
  // не знает — тогда человеку нужно «Подключить», а не «Отключить».
  const currentDeviceId =
    items.find((item) => item.endpoint_fingerprint === devices.data?.current)
      ?.id ?? null;
  const subscribedHere = currentDeviceId !== null;
  // Проверку доставки отправляет системная ручка: она есть не у всех.
  const canProbe = services.operations?.testNotification !== undefined;
  // Пока не подключено ни одного устройства, столбец «на устройство» ничего не
  // меняет: слать некуда.
  const pushPossible = items.length > 0;
  // Одно слово в свёрнутой шапке: подключено ли это устройство. Без открытия блока
  // видно, требует ли он внимания.
  const permissionNow = manager.permission();
  const deviceState: { tone: "ready" | "pending" | "rejected"; label: string } | null =
    permissionNow === "denied"
      ? { tone: "rejected", label: "Заблокированы" }
      : devices.data === undefined
        ? null
        : subscribedHere
          ? { tone: "ready", label: "Подключено" }
          : { tone: "pending", label: "Не подключено" };

  return (
    <section
      className={`notification-settings${expanded ? "" : " notification-settings--collapsed"}`}
      aria-labelledby="notification-settings-title"
    >
      <h2 id="notification-settings-title" className="notification-settings__title">
        <button
          className="notification-settings__toggle"
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded((value) => !value)}
        >
          <span className="notification-settings__label">Настройки уведомлений</span>
          {deviceState === null ? null : (
            <span className={`status-badge status-badge--${deviceState.tone}`}>{deviceState.label}</span>
          )}
          <HiChevronDown aria-hidden="true" />
        </button>
      </h2>
      <div id={panelId} className="notification-settings__panel" hidden={!expanded}>
      <header className="notification-settings__head">
        <div>
          <p className="dw-secondary">{summary(devices.data, subscribedHere)}</p>
          <p className="dw-secondary">{permissionHint(manager.permission())}</p>
        </div>
        <div className="inline-actions">
          {subscribedHere ? (
            <button
              className="button button--secondary button--compact"
              type="button"
              disabled={disconnect.isPending}
              onClick={() => {
                setNotice(null);
                if (currentDeviceId !== null) {
                  disconnect.mutate({ id: currentDeviceId, here: true });
                }
              }}
            >
              {disconnect.isPending
                ? "Отключаем…"
                : "Отключить это устройство"}
            </button>
          ) : (
            <button
              className="button button--secondary button--compact"
              type="button"
              disabled={connect.isPending}
              onClick={() => {
                setNotice(null);
                connect.mutate();
              }}
            >
              {connect.isPending ? "Подключаем…" : "Подключить это устройство"}
            </button>
          )}
          {canProbe ? (
            <button
              className="button button--secondary button--compact"
              type="button"
              disabled={probe.isPending}
              onClick={() => {
                setNotice(null);
                probe.mutate();
              }}
            >
              {probe.isPending ? "Отправляем…" : "Проверить уведомления"}
            </button>
          ) : null}
          <button
            className="button button--text button--compact"
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? "Свернуть" : "Настроить"}
          </button>
        </div>
      </header>

      {notice ? (
        <div className="operation-notice">
          <Notice role="status">{notice}</Notice>
          {offeredGeneral !== null ? (
            <button
              className="button button--secondary button--compact"
              type="button"
              disabled={toggle.isPending}
              onClick={() =>
                toggle.mutate(offeredGeneral, {
                  onSuccess: () => {
                    setOfferedGeneral(null);
                    setNotice(
                      "Включено: уведомления с сервера теперь будут приходить на подключённые устройства. Нажмите «Проверить уведомления» ещё раз — должно появиться два окна.",
                    );
                  },
                })
              }
            >
              Включить «На устройство» для всех событий
            </button>
          ) : null}
        </div>
      ) : null}

      <NotificationDeviceHelp open={helpOpen} onOpenChange={setHelpOpen} />

      <NotificationDiagnostics services={services} />

      {open ? (
        <>
          {preferences.isError ? (
            <Notice>
              Не удалось загрузить настройки. Обновите страницу.
            </Notice>
          ) : null}

          {/*
            Своя раскладка, а не общий `users-table`: у того на телефоне свои
            правила (прячет третий и четвёртый столбцы, сдвигает вторую ячейку),
            и галочки вылезали за край экрана.
          */}
          <div className="notification-matrix">
            <table className="notification-settings__table">
              <thead>
                <tr>
                  <th scope="col">Событие</th>
                  <th scope="col">В центре</th>
                  <th scope="col">В браузере</th>
                  <th scope="col">На устройство</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.type} className={row.type === "*" ? "notification-settings__general" : undefined}>
                    <th scope="row">
                      <strong>{row.label}</strong>
                      <small className="dw-secondary">
                        {row.type === "*"
                          ? "Действует на события, у которых нет своей настройки"
                          : row.configured
                            ? "Настроено отдельно"
                            : "Как во «Всех событиях»"}
                      </small>
                    </th>
                    {(["in_app", "realtime", "web_push"] as const).map((channel) => (
                      <td key={channel}>
                        <label className="notification-settings__channel">
                          <span>{channelLabel(channel)}</span>
                          <input
                            type="checkbox"
                            aria-label={`${row.label}: ${channelLabel(channel)}`}
                            checked={row[channel]}
                            disabled={toggle.isPending || (channel === "web_push" && !pushPossible)}
                            onChange={() => {
                              setNotice(null);
                              toggle.mutate({
                                type: row.type,
                                in_app: row.in_app,
                                realtime: row.realtime,
                                web_push: row.web_push,
                                [channel]: !row[channel],
                              });
                            }}
                          />
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!pushPossible ? (
            <p className="dw-secondary">
              Столбец «на устройство» станет доступен, когда будет подключено хотя бы одно
              устройство.
            </p>
          ) : general?.web_push === false ? (
            <p className="dw-secondary">
              Во «Всех событиях» уведомления на устройство выключены: придут только те события, где
              вы включили этот столбец отдельно.
            </p>
          ) : null}

          <h3 className="notification-settings__devices-title">Устройства</h3>
          {items.length === 0 ? (
            <p className="dw-secondary">
              Ни одного устройства не подключено. Уведомления приходят, только пока ERP открыта.
            </p>
          ) : (
            <ul className="notification-settings__devices">
              {items.map((device) => {
                const here = device.endpoint_fingerprint === devices.data?.current;
                return (
                  <li key={device.id}>
                    <span>
                      <strong>{device.device_name ?? "Устройство без имени"}</strong>
                      {here ? <span className="status-badge status-badge--ready">Это устройство</span> : null}
                      <small className="dw-secondary">{connectedAt(device.created_at)}</small>
                      <small className={device.last_used_at === null ? "dw-secondary notification-settings__never" : "dw-secondary"}>
                        {deliveredAt(device.last_used_at)}
                      </small>
                    </span>
                    <button
                      className="button button--text button--compact"
                      type="button"
                      disabled={disconnect.isPending}
                      onClick={() => {
                        setNotice(null);
                        disconnect.mutate({ id: device.id, here });
                      }}
                    >
                      Отключить
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : null}
      </div>
    </section>
  );
}

/** Что браузер ответил на просьбу показывать уведомления — человеческими словами. */
function permissionHint(permission: NotificationPermission | "unsupported"): string {
  switch (permission) {
    case "granted":
      return "Браузер показывает уведомления. Если всплывающих окон не видно, откройте подсказку «Не видно уведомлений?» ниже.";
    case "denied":
      return currentPlatform() === "ios"
        ? "Уведомления отклонены. Откройте «Настройки» → «Уведомления» → ERP, включите «Допуск уведомлений» и подключите устройство заново."
        : "Браузер не показывает уведомления: разрешение отклонено. Разрешите их для этого сайта в настройках браузера и подключите устройство заново — как это сделать, написано ниже.";
    case "unsupported":
      return currentPlatform() === "ios" && !isStandalone()
        ? "На iPhone и iPad уведомления работают только в установленном веб-приложении. Установите ERP на экран «Домой» — инструкция в разделе «О приложении» — и откройте её с иконки."
        : "Этот браузер не умеет показывать уведомления с закрытой вкладкой. На iPhone и iPad это работает только в установленном веб-приложении.";
    default:
      return "Разрешение на уведомления ещё не запрашивалось: нажмите «Подключить это устройство».";
  }
}

/** Одна строка о том, куда сейчас приходят уведомления. */
function summary(state: { items: unknown[] } | undefined, subscribedHere: boolean): string {
  if (state === undefined) return "Проверяем, куда приходят уведомления…";
  const count = state.items.length;
  if (subscribedHere) {
    const others = count - 1;
    return others > 0
      ? `Приходят на это устройство и ещё на ${others}, даже когда ERP закрыта.`
      : "Приходят на это устройство, даже когда ERP закрыта.";
  }
  if (count > 0) {
    return `На это устройство не приходят, но подключено других: ${count}.`;
  }
  return "Приходят только пока ERP открыта. Подключите устройство, чтобы получать их и с закрытой вкладкой.";
}

export function NotificationsPage({
  services,
  settingsExpanded = false,
}: {
  services: NotificationServices;
  /** Раскрыт ли блок настроек при открытии страницы; по умолчанию он свёрнут. */
  settingsExpanded?: boolean;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<SystemNotification | null>(null);
  const api = services.notifications;
  const list = useQuery({ queryKey: ["notifications", "list"], queryFn: () => api.list() });
  const readAll = useMutation({
    mutationFn: () => api.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const items = notificationItems(list.data);

  return (
    <section className="workspace-page notifications-page" aria-labelledby="notifications-page-title">
      <div className="page-heading page-heading--split">
        <div>
          <p className="eyebrow">Центр уведомлений</p>
          <h1 id="notifications-page-title">Уведомления</h1>
          <p>События из документов, переписки и системных процессов.</p>
        </div>
        <div className="page-actions">
          <button
            className="button button--primary"
            disabled={items.length === 0 || readAll.isPending}
            onClick={() => readAll.mutate()}
          >
            Прочитать все
          </button>
        </div>
      </div>

      <NotificationSettings services={services} defaultExpanded={settingsExpanded} />

      <div className="notifications-page__list">
        {list.isError ? (
          <p className="empty-state">Не удалось загрузить уведомления. Обновите страницу.</p>
        ) : items.length === 0 ? (
          <p className="empty-state">Новых уведомлений нет.</p>
        ) : (
          items.map((item) => (
            <button
              className={`notification notification-card-button notification--${item.priority} ${item.read_at === null ? "notification--unread" : ""}`}
              key={item.id}
              onClick={() => setSelected(item)}
            >
              {item.read_at === null ? <span className="visually-hidden">Не прочитано. </span> : null}
              <strong>{item.title}</strong>
              <p>{item.body}</p>
            </button>
          ))
        )}
      </div>
      {selected ? <NotificationDetail item={selected} onClose={() => setSelected(null)} /> : null}
    </section>
  );
}
